/**
 * Respuestas guardadas (canned responses) por cliente.
 * Se usan desde el composer del inbox para responder más rápido.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiDelete } from "@/lib/api";

export interface SavedReply {
  id: number;
  clientSlug: string;
  title: string;
  body: string;
  usageCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useSavedReplies(clientSlug: string | undefined) {
  return useQuery<SavedReply[]>({
    queryKey: ["saved-replies", clientSlug],
    enabled: !!clientSlug,
    queryFn: async () => {
      const res = await apiGet<{ items: SavedReply[] }>(
        `/saved-replies?clientSlug=${encodeURIComponent(clientSlug!)}`,
      );
      return res.items;
    },
  });
}

export function useCreateSavedReply() {
  const qc = useQueryClient();
  return useMutation<
    { reply: SavedReply },
    Error,
    { clientSlug: string; title: string; body: string }
  >({
    mutationFn: (payload) => apiPost<{ reply: SavedReply }>("/saved-replies", payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["saved-replies", vars.clientSlug] });
    },
  });
}

export function useDeleteSavedReply(clientSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { id: number }>({
    mutationFn: ({ id }) => apiDelete<{ ok: boolean }>(`/saved-replies/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-replies", clientSlug] });
    },
  });
}

/** Registra el uso de una respuesta (para el orden "usado frecuentemente"). */
export function useMarkSavedReplyUsed(clientSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { id: number }>({
    mutationFn: ({ id }) => apiPost<{ ok: boolean }>(`/saved-replies/${id}/use`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-replies", clientSlug] });
    },
  });
}
